/*
Vehicle Publishing Queue Interfaces
Defines the core types for the sequential vehicle publishing queue system
Author: Assistant
Date: 2025-06-24
*/

export interface QueueVehicleItem {
  vehicleId: string
  vehicle: any // Will be properly typed in Phase 2
  branch: any // Will be properly typed in Phase 2
  formData?: {
    title?: string
    price?: number
    comments?: string
    commune?: string
    type?: string
    brand?: string
    model?: string
    fuel?: string
    body?: string
    gearbox?: string
  }
}

export interface QueueItem {
  id: string // Unique identifier for this queue item
  vehicleId: string
  vehicle: any
  branch: any
  formData?: QueueVehicleItem['formData']
  status: QueueItemStatus
  error?: string
  result?: QueueItemResult
  timestamp: {
    queued: Date
    started?: Date
    completed?: Date
  }
  retryCount: number
  maxRetries: number
  // NEW: Support for unpublish operations
  operation: 'publish' | 'unpublish'
  platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'>
}

export type QueueItemStatus =
  | 'pending' // Waiting to be processed
  | 'processing' // Currently being published
  | 'completed' // Successfully published
  | 'failed' // Failed after all retries
  | 'cancelled' // Manually cancelled
  | 'paused' // Paused by user

export interface QueueItemResult {
  success: boolean
  inventoryIdentifier?: string
  permalink?: string
  title?: string
  message?: string
  errorDetails?: any
}

// NEW: Unpublish-specific result interfaces
export interface UnpublishResult {
  platform: 'mercadolibre' | 'chileautos' | 'yapo'
  success: boolean
  error?: string
  wasPublished: boolean // Whether vehicle was actually published on this platform
}

export interface BulkUnpublishResult {
  vehicleId: string
  platforms: UnpublishResult[]
  overallSuccess: boolean
}

export interface QueueState {
  items: QueueItem[]
  status: QueueStatus
  currentIndex: number
  statistics: QueueStatistics
  settings: QueueSettings
}

export type QueueStatus =
  | 'idle' // No items or not processing
  | 'processing' // Currently processing items
  | 'paused' // Processing paused by user
  | 'completed' // All items processed
  | 'cancelled' // Queue cancelled by user

export interface QueueStatistics {
  totalItems: number
  completedCount: number
  failedCount: number
  pendingCount: number
  processingCount: number
  cancelledCount: number
  estimatedTimeRemaining?: number // in milliseconds
  averageProcessingTime?: number // in milliseconds
  startTime?: Date
  endTime?: Date
}

export interface QueueSettings {
  delayBetweenItems: number // milliseconds
  maxConcurrentItems: number // Should be 1 for sequential processing
  autoRetryFailed: boolean
  maxRetries: number
  pauseOnError: boolean
  persistQueue: boolean // Save queue state to localStorage
}

export interface QueueActionPayload {
  type: QueueActionType
  payload?: any
}

export type QueueActionType =
  | 'ADD_ITEMS'
  | 'REMOVE_ITEM'
  | 'CLEAR_QUEUE'
  | 'START_PROCESSING'
  | 'PAUSE_PROCESSING'
  | 'RESUME_PROCESSING'
  | 'CANCEL_PROCESSING'
  | 'UPDATE_ITEM_STATUS'
  | 'UPDATE_STATISTICS'
  | 'UPDATE_SETTINGS'
  | 'RETRY_FAILED_ITEMS'
  | 'RESET_QUEUE'

export interface QueueEventCallbacks {
  onItemStart?: (item: QueueItem) => void
  onItemComplete?: (item: QueueItem, result: QueueItemResult) => void
  onItemFailed?: (item: QueueItem, error: string) => void
  onQueueComplete?: (statistics: QueueStatistics) => void
  onQueuePaused?: () => void
  onQueueResumed?: () => void
  onQueueCancelled?: () => void
  onProgress?: (progress: number) => void // 0-100
}

// Default settings
export const DEFAULT_QUEUE_SETTINGS: QueueSettings = {
  delayBetweenItems: 2000, // 2 seconds
  maxConcurrentItems: 1, // Sequential processing
  autoRetryFailed: true,
  maxRetries: 3,
  pauseOnError: false,
  persistQueue: true,
}

// Utility type for queue operations
export interface QueueOperations {
  addItems: (vehicles: QueueVehicleItem[]) => void
  removeItem: (itemId: string) => void
  clearQueue: () => void
  startProcessing: () => Promise<void>
  pauseProcessing: () => void
  resumeProcessing: () => void
  cancelProcessing: () => void
  retryFailedItems: () => void
  retryItem: (itemId: string) => void
  updateSettings: (settings: Partial<QueueSettings>) => void
  resetQueue: () => void
}
